import { mainLogger as log } from "@/app/logger"
import { GraphData } from "@/app/types/chart_data"
import { theme } from "@/tailwind.config"

// this are props that are common to all types of charts
export type Props = {
  data: GraphData
  isEntityEnabled: boolean
  isComparisonEnabled: boolean
  reverse?: boolean
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  showTicker?: boolean
}

// Be careful to use objects in the common options because we use the
// spread operator to "copy" these options, and it only goes one level
// deep, so objects will be overwritten (not merged) just like other
// properties. They are ok to use if they are not overridden/merged.

export const TOP_LEVEL_OPTIONS = {
  title: null,
  subtitle: null,
  credits: { enabled: false },
  legend: { enabled: false },
  accessibility: { enabled: false }, // TODO - fix this later
}

// see https://api.highcharts.com/highcharts/chart
export const CHART_OPTIONS = {
  // type: "bar", // override this in the specific chart component
  // backgroundColor: "#ffffff", // default #ffffff
  displayErrors: false, // When false, the errors will be shown only in the console.
  // disable browser handling of all touch-action's within the chart area
  // pan / pinch will be handled by the Highcharts library
  style: { "touch-action": "none" },
  // see https://api.highcharts.com/highcharts/chart.zooming
  // zooming/panning example - https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/chart/pankey/
  zooming: {
    type: "x",
    pinchType: "xy",
  },
  // see https://api.highcharts.com/highcharts/chart.panning
  panning: { enabled: true, type: "xy" },
  panKey: "ctrl",
}

// see https://api.highcharts.com/highcharts/tooltip
export const TOOLTIP_OPTIONS = {
  // the following will ensure that toolip follows the mouse/touch
  // on mobile devices, the zoom/pan is taken over by 2 fingers gestures
  // ID 11/6/23 - disable it so that we can use single finger to pan
  followTouchMove: true,
  followPointer: true, // disabled so the tooltip doesnt move with the mouse when visible
  // default SVG to create for the tooltip
  shape: "rect",
  //backgroundColor: "rgba(250,250,250,1)",
  borderColor: theme.colors.main[500],
  borderRadius: 10,
  borderWidth: 1,
  // disable the tooltip animation (jarring on mobile)
  // remove shadow
  animation: false,
  shadow: false,
  // allow the user to see the tooltip from anywhere in the plot ares
  // also for multiple series (e.g. positioning charts) dont show multiple tooltips
  shared: true,
  split: false,
}

// https://api.highcharts.com/highcharts/xAxis.crosshair
export const CROSSHAIR_OPTIONS = {
  width: 1,
  color: theme.colors["main-dark"],
  dashStyle: "dash",
  snap: true,
}

export const X_AXES_LABELS = {
  enabled: true,
  padding: 0,
  allowOverlap: false,
  autoRotation: [10],
  autoRotationLimit: 50,
  overflow: "justify",
  style: {
    whiteSpace: "nowrap",
    color: theme.colors.main[400],
    fontSize: "0.75rem",
  },
}

// explore BREAKS
// https://jsfiddle.net/oz1wyjmp/
// https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/axisbreak/break-visualized/
// https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/axisbreak/break-simple/

export function addAsOfLabel(
  chart: any,
  label: string,
  xPosition: number,
  yPosition: number,
  fontSize: string = "0.75rem"
) {
  // create the single "as-of" annotation, that can be moved up (if needed)
  log.trace("ASOF CHART", chart)
  const x = xPosition
  const y = yPosition

  const annotation = {
    id: "asof",
    draggable: "",
    labels: [
      {
        text: label,
        shape: "rect",
        backgroundColor: "transparent",
        style: {
          color: theme.colors.main[400],
          fontSize: fontSize,
        },
        borderWidth: 0,
        point: { x, y },
      },
    ],
  }
  log.trace("ADDING AS_OF_DATE ANNOTATION", annotation)
  chart.addAnnotation(annotation)
}

export function hexToRGBA(hex, opacity) {
  hex = hex.replace("#", "")

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export function createResetZoomButtonEvents(chart) {
  const container = chart.container

  // container.addEventListener("wheel", () => {
  //   if (!chart.resetZoomButton) {
  //     chart.showResetZoom()
  //   }
  // })

  container.addEventListener("touchstart", (event) => {
    if (event.touches.length >= 2 && !chart.resetZoomButton) {
      chart.showResetZoom()
    }
  })

  container.addEventListener("touchmove", (event) => {
    if (event.touches.length >= 2 && !chart.resetZoomButton) {
      chart.showResetZoom()
    }
  })
}

// PRINTING MODE RESIZING FUNCTIONS
function redimChartBeforePrint(isScreenMobile: boolean) {
  const elements = [
    document.getElementsByClassName("highcharts-root")[0] as HTMLElement,
    document.getElementsByClassName("highcharts-container")[0] as HTMLElement,
    document.getElementsByClassName("highcharts-background")[0] as HTMLElement,
  ]

  elements.forEach((element) => {
    if (element) {
      element.style.width = "100%"
      element.style.height = isScreenMobile ? "500px" : "100%"
    }
  })
}

function redimChartAfterPrint() {
  const elements = [
    document.getElementsByClassName("highcharts-root")[0] as HTMLElement,
    document.getElementsByClassName("highcharts-container")[0] as HTMLElement,
    document.getElementsByClassName("highcharts-background")[0] as HTMLElement,
  ]

  elements.forEach((element) => {
    if (element) {
      element.style.removeProperty("width")
      element.style.removeProperty("height")
    }
  })
}

export function redimChartForPrint(isScreenMobile: boolean) {
  window.addEventListener("beforeprint", () =>
    redimChartBeforePrint(isScreenMobile)
  )
  window.addEventListener("afterprint", () => redimChartAfterPrint())

  return () => {
    window.removeEventListener("beforeprint", () =>
      redimChartBeforePrint(isScreenMobile)
    )
    window.removeEventListener("afterprint", () => redimChartAfterPrint())
  }
}
